/****************************************************************************
 * COPYRIGHT (c) 2024 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/
import APIManager from "../../ApiManager/ApiManager";
import {
  FETCH_VEHICLES_DETAILS_REQUEST,
  FETCH_VEHICLES_DETAILS_SUCCESS,
  FETCH_VEHICLES_DETAILS_FAILURE,
  FETCH_DOMAIN_APPROVER_VEHICLES_REQUEST,
  FETCH_DOMAIN_APPROVER_VEHICLES_SUCCESS,
  FETCH_DOMAIN_APPROVER_VEHICLES_FAILURE,
} from "./VehiclesDetailsTypes";
import { COMMON_ERROR } from "../ApplicationDetailsError/ApplicationDetailsErrorTypes";

const VEHICLES_CACHE_KEY = 'vehicles-cache';
const VEHICLES_CACHE_TIMESTAMP_KEY = 'vehicles-cache-timestamp';
const CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes

const safeParseCache = () => {
  if (typeof sessionStorage === 'undefined') return null;

  try {
    const cached = sessionStorage.getItem(VEHICLES_CACHE_KEY);
    const timestamp = sessionStorage.getItem(VEHICLES_CACHE_TIMESTAMP_KEY);
    
    if (!cached || !timestamp) return null;

    // Check if cache has expired
    const cacheTime = Number.parseInt(timestamp, 10);
    const currentTime = Date.now();
    if (currentTime - cacheTime > CACHE_DURATION_MS) {
      // Cache expired
      sessionStorage.removeItem(VEHICLES_CACHE_KEY);
      sessionStorage.removeItem(VEHICLES_CACHE_TIMESTAMP_KEY);
      return null;
    }

    const parsed = JSON.parse(cached);
    return Array.isArray(parsed) ? parsed : null;
  } catch (error) {
    console.warn('Failed to parse vehicles cache', error);
    return null;
  }
};

const persistCache = (vehicles) => {
  if (typeof sessionStorage === 'undefined') return;

  try {
    sessionStorage.setItem(VEHICLES_CACHE_KEY, JSON.stringify(vehicles));
    sessionStorage.setItem(VEHICLES_CACHE_TIMESTAMP_KEY, Date.now().toString());
  } catch (error) {
    console.warn('Failed to persist vehicles cache', error);
  }
};

export const fetchVehiclesDetailsRequest = () => {
  return {
    type: FETCH_VEHICLES_DETAILS_REQUEST,
  };
};

export const fetchVehiclesDetailsSuccess = (vehiclesData) => {
  return {
    type: FETCH_VEHICLES_DETAILS_SUCCESS,
    payload: vehiclesData,
  };
};

export const fetchVehiclesDetailsFailure = (error) => {
  return {
    type: FETCH_VEHICLES_DETAILS_FAILURE,
    payload: error,
  };
};

export const fetchVehiclesDetails = () => {
  return async function (dispatch) {
    // Check cache first
    const cachedVehicles = safeParseCache();
    if (cachedVehicles && cachedVehicles.length > 0) {
      dispatch(fetchVehiclesDetailsSuccess(cachedVehicles));
      return cachedVehicles;
    }

    dispatch(fetchVehiclesDetailsRequest());
    try {
      const data = await APIManager.getVehicles();
      persistCache(data);
      dispatch(fetchVehiclesDetailsSuccess(data));
    } catch (error) {
      dispatch(fetchVehiclesDetailsFailure(error));
      dispatch(commonError(error, FETCH_VEHICLES_DETAILS_FAILURE));
    }
  };
};

export const commonError=(error,errorType)=>{
  return({
    type:COMMON_ERROR,
    payload:{
      message:error.message,
      errorType:errorType
    }
  })
}

// Domain Approver Vehicles Actions
export const fetchDomainApproverVehiclesRequest = () => {
  return {
    type: FETCH_DOMAIN_APPROVER_VEHICLES_REQUEST,
  };
};

export const fetchDomainApproverVehiclesSuccess = (vehiclesData) => {
  return {
    type: FETCH_DOMAIN_APPROVER_VEHICLES_SUCCESS,
    payload: vehiclesData,
  };
};

export const fetchDomainApproverVehiclesFailure = (error) => {
  return {
    type: FETCH_DOMAIN_APPROVER_VEHICLES_FAILURE,
    payload: error,
  };
};

export const fetchDomainApproverVehicles = (domainAliasId) => {
  return async function (dispatch) {
    dispatch(fetchDomainApproverVehiclesRequest());
    try {
      const data = await APIManager.getDomainApproverVehicles(domainAliasId);
      dispatch(fetchDomainApproverVehiclesSuccess(data));
    } catch (error) {
      dispatch(fetchDomainApproverVehiclesFailure(error));
      dispatch(commonError(error, FETCH_DOMAIN_APPROVER_VEHICLES_FAILURE));
    }
  };
};
